import Component from '@glimmer/component';
import { service } from '@ember/service';
import AddSomethingModal from 'donor-elf-web/services/add-something-modal';
import type { OpenArgs } from 'donor-elf-web/services/add-something-modal';
import Store from '@ember-data/store';
import { assert } from '@ember/debug';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactModel from 'donor-elf-web/models/contact';
import {
  MergeProperties,
  AddressProperties,
  createMergeChangesetFromContact
} from 'donor-elf-web/lib/merge-contacts-utils';
import { createCustomPropertyChangeset } from 'donor-elf-web/lib/merge-contacts-utils';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import lifecycle from 'donor-elf-web/modifiers/lifecycle';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq, notEq, and } from 'ember-truth-helpers';
import isPresentHelper from 'donor-elf-web/helpers/is-present';
import ModalHeader from 'donor-elf-web/components/modal/header';
import ModalFooter from 'donor-elf-web/components/modal/footer';
import GenericError from 'donor-elf-web/components/generic-error';
import AlertDanger from 'donor-elf-web/components/alert/danger';
import MergeProperty from 'donor-elf-web/components/add-something-modal/merge-contacts/merge-property';
import MergeAddressProperties from 'donor-elf-web/components/add-something-modal/merge-contacts/merge-address-properties';
import MergePhonesOrEmails from 'donor-elf-web/components/add-something-modal/merge-contacts/merge-phones-or-emails';
import MergeGroups from 'donor-elf-web/components/add-something-modal/merge-contacts/merge-groups';
import MergeCustomProperty from 'donor-elf-web/components/add-something-modal/merge-contacts/merge-custom-property';
import AddNamesQuestions from 'donor-elf-web/components/add-something-modal/merge-contacts/add-names-questions';
import Merging from 'donor-elf-web/components/add-something-modal/merge-contacts/merging';

interface ComponentArgs {
  openArgs: OpenArgs;
}

interface CustomPropertyItem {
  customPropertyInfo: ContactCustomPropertyInfo;
  customPropertyChangeset: any;
}

export default class AddSomethingModalMergeContactsIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    assert("2 contact ids must be passed in", args.openArgs.mergeContactsOptions?.contactIds.length === 2);
  }

  @service addSomethingModal!: AddSomethingModal;
  @service contactsCache!: ContactsCache;
  @service store!: Store;

  @tracked stage: 'selectContact' | 'selectValues' | 'addNamesQuestions' | 'merging' | 'mergingError' = 'selectContact';
  @tracked contactToKeep: ContactModel;
  @tracked contactToMerge: ContactModel;
  @tracked subHeading = 'Select the main contact record to keep';
  @tracked customPropertyItems?: CustomPropertyItem[];
  @tracked showValidationErrors = false;

  changeset: any;

  get contacts() {
    return this.args.openArgs.mergeContactsOptions?.contactIds.map(contactId => {
      return this.contactsCache.contactsById[contactId];
    })!;
  }

  get eitherContactHasEmails() {
    return this.contacts.some(contact => {
      return contact.hasEmails;
    });
  }

  get propertiesToMerge() {
    const anyDeceasedPropertyIsTrue = (
      this.contactToKeep.deceased ||
      this.contactToKeep.spouseDeceased ||
      this.contactToMerge.deceased ||
      this.contactToMerge.spouseDeceased
    );

    return MergeProperties.filter(x => {
      if (x.property === 'deceased' || x.property === 'spouseDeceased') {
        // only show deceased fields if at least one of the contacts is deceased
        return anyDeceasedPropertyIsTrue;
      }

      return true;
    });
  }

  get addressPropertiesPresent() {
    const { contactToKeep, contactToMerge } = this;
    return AddressProperties.some(x => {
      return isPresent(contactToKeep[x.property]) || isPresent(contactToMerge[x.property]);
    });
  }

  get customPropertiesToMerge() {
    const { contactToKeep, contactToMerge } = this;
    return this.contactsCache.sortedCustomPropertiesForDetailPage.filter(x => {
      return (
        x.l !== 'Groups' &&
        (
          isPresent(contactToKeep.customPropertyValue(x.l)) ||
          isPresent(contactToMerge.customPropertyValue(x.l))
        )
      );
    });
  }

  createCustomPropertyItems() {
    this.customPropertyItems = this.customPropertiesToMerge.map((customPropertyInfo) => {
      return {
        customPropertyInfo,
        customPropertyChangeset: createCustomPropertyChangeset({
          contactToKeep: this.contactToKeep,
          contactToMerge: this.contactToMerge,
          contactsCache: this.contactsCache,
          customPropertyInfo,
        })
      };
    });
  }

  setContactToKeep = (contact: ContactModel) => {
    this.contactToKeep = contact;
    this.contactToMerge = this.contacts.find(x => x !== contact)!;
    this.changeset = createMergeChangesetFromContact(this.contactToKeep);
    this.createCustomPropertyItems();
    this.subHeading = 'Edit the values to keep';
    this.stage = 'selectValues';
  }

  validateChangeset = async (): boolean => {
    await this.changeset.validate();
    let isValid = this.changeset.isValid;

    // Validate custom properties
    for (const item of this.customPropertyItems || []) {
      await item.customPropertyChangeset.validate();
      isValid = isValid && item.customPropertyChangeset.isValid;
    }
    this.showValidationErrors = !isValid;
    return isValid;
  }

  get propertyNamesWithValidationErrors() {
    const names = new Set<string>();
    if (this.showValidationErrors) {
      this.changeset.errors.forEach(x => names.add(x.key));
      for (const item of this.customPropertyItems || []) {
        if (item.customPropertyChangeset.isValid === false) {
          names.add(item.customPropertyInfo.l);
        }
      }
    }
    return Array.from(names);
  }

  copyCustomPropertyChangesToChangeset() {
    if (this.customPropertyItems) {
      const customProperties = [];
      for (const item of this.customPropertyItems) {
        item.customPropertyChangeset.execute();
        customProperties.push(item.customPropertyChangeset.data)
      }
      if (this.changeset.get('custom') == null) {
        this.changeset.set('custom', {});
      }
      this.changeset.set('custom.properties', customProperties);
    }
  }

  finishedSettingValuesToKeep = async (e: any) => {
    if ((await this.validateChangeset()) == false) {
      this.scrollToBottom(e.target);
      return;
    }
    this.copyCustomPropertyChangesToChangeset();

    this.subHeading = 'Tasks and Pledges';
    this.stage = 'addNamesQuestions';
  }

  merge = () => {
    this.subHeading = 'Merging';
    this.stage = 'merging';
  }

  handleMergingCompleted = async () => {
    await this.contactToKeep.reload();
    this.contactsCache.removeContactId(this.contactToMerge.id);
    this.addSomethingModal.close();
    this.addSomethingModal.contactsMerged({
      contactIdKept: this.contactToKeep.id,
      contactIdMerged: this.contactToMerge.id
    });
  }

  handleMergingError = () => {
    this.subHeading = 'Merge Error';
    this.stage = 'mergingError';
  }

  close = () => {
    this.addSomethingModal.close();
  }

  scrollToTop = (el: HTMLElement) => {
    setTimeout(() => {
      el.scrollTop = 0;
    });
  }

  scrollToBottom = (el: HTMLElement) => {
    const scrollContainer = el.closest('[data-scroll-to-bottom]');
    setTimeout(() => {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
  }

  <template>
    <div>
      <ModalHeader>
        Merge Contacts
      </ModalHeader>
      <div class='py-2 px-3 border-bottom bg-gray-100'>
        <div class='tw-text-lg'>{{this.subHeading}}</div>
      </div>
      {{#if (eq this.stage 'selectContact')}}
        <div
          {{lifecycle this.scrollToTop}}
          class='p-3 overflow-auto' style='max-height: 85vh' {{!-- not going to create a class for this specific style --}}
        >
          <div class='d-inline-block'>
            <div class='tw-grid tw-grid-cols-2 tw-gap-x-12'>
              {{#each this.contacts as |contact|}}
                <div>
                  <strong>{{contact.name}}</strong>
                </div>
              {{/each}}
              {{#each this.contacts as |contact|}}
                <div class='tw-text-sm tw-leading-tight'>
                  <div>{{contact.address1}}</div>
                  <div>{{contact.address2}}</div>
                  {{#if contact.cityAndState}}
                    <div>{{contact.cityAndState}} {{contact.zip}}</div>
                  {{/if}}
                </div>
              {{/each}}
              {{#if this.eitherContactHasEmails}}
                {{#each this.contacts as |contact|}}
                  <div class='mt-2 tw-text-sm tw-leading-tight'>
                    {{#each contact.emails.data as |item|}}
                      <div>{{item.v}}</div>
                    {{/each}}
                  </div>
                {{/each}}
              {{/if}}
              {{#each this.contacts as |contact|}}
                <div class='mt-3'>
                  <button
                    {{on 'click' (fn this.setContactToKeep contact)}}
                    class='btn btn-sm btn-brand'
                    type='button'
                  >
                    Keep this Contact
                  </button>
                </div>
              {{/each}}
            </div>
          </div>
        </div>
      {{else if (eq this.stage 'selectValues')}}
        <div
          {{lifecycle this.scrollToTop}}
          class='p-3 overflow-auto' style='max-height: 85vh' {{!-- not going to create a class for this specific style --}}
          data-scroll-to-bottom
        >
          <p>
            Select or edit the values to keep for the contact you selected. The
            right column shows the current values for the 2 contacts being
            merged for your reference.
          </p>
          <p>
            Once you have selected the values to keep, scroll to the bottom and
            click the <strong>Next</strong> button.
          </p>
          {{#each this.propertiesToMerge as |info index|}}
            <MergeProperty
              @changeset={{this.changeset}}
              @property={{info.property}}
              @propertyLabel={{info.label}}
              @propertyIsBoolean={{info.boolean}}
              @contactToKeep={{this.contactToKeep}}
              @contactToMerge={{this.contactToMerge}}
              @showValidationErrors={{this.showValidationErrors}}
              class='{{if (eq 0 index) 'border-top'}}'
            />
          {{/each}}
          {{#if this.addressPropertiesPresent}}
            <MergeAddressProperties
              @changeset={{this.changeset}}
              @contactToKeep={{this.contactToKeep}}
              @contactToMerge={{this.contactToMerge}}
            />
          {{/if}}
          <MergePhonesOrEmails
            @changeset={{this.changeset}}
            @contactToKeep={{this.contactToKeep}}
            @contactToMerge={{this.contactToMerge}}
            @property='phones'
          />
          <MergePhonesOrEmails
            @changeset={{this.changeset}}
            @contactToKeep={{this.contactToKeep}}
            @contactToMerge={{this.contactToMerge}}
            @property='emails'
          />
          <MergeGroups
            @changeset={{this.changeset}}
            @property='groups'
            @propertyLabel='Groups'
            @contactToKeep={{this.contactToKeep}}
            @contactToMerge={{this.contactToMerge}}
          />
          {{#each this.customPropertyItems as |item|}}
            <MergeCustomProperty
              @customPropertyInfo={{item.customPropertyInfo}}
              @customPropertyChangeset={{item.customPropertyChangeset}}
              @contactToKeep={{this.contactToKeep}}
              @contactToMerge={{this.contactToMerge}}
              @showValidationErrors={{this.showValidationErrors}}
            />
          {{/each}}
          {{#if (and this.showValidationErrors (isPresentHelper this.propertyNamesWithValidationErrors))}}
            <AlertDanger class='mt-3 tw-text-sm'>
              <div class='mb-2'>
                Please fix the errors for the following properties:
              </div>
              <ul>
                {{#each this.propertyNamesWithValidationErrors as |name|}}
                  <li class='-tw-ml-6'>{{name}}</li>
                {{/each}}
              </ul>
            </AlertDanger>
          {{/if}}
          <div class='mt-3'>
            <button
              {{on 'click' this.finishedSettingValuesToKeep}}
              class='btn btn-sm btn-warning'
              type='button'
            >
              Next
            </button>
          </div>
        </div>
      {{else if (eq this.stage 'addNamesQuestions')}}
        <div
          {{lifecycle this.scrollToTop}}
          class='p-3 overflow-auto' style='max-height: 85vh' {{!-- not going to create a class for this specific style --}}
        >
          <AddNamesQuestions
            @changeset={{this.changeset}}
          />
          <div class='mt-4'>
            <button
              {{on 'click' this.merge}}
              class='btn btn-sm btn-warning'
              type='button'
            >
              Merge
            </button>
          </div>
        </div>
      {{else if (eq this.stage 'merging')}}
        <div class='p-3'>
          <Merging
            @contactToKeep={{this.contactToKeep}}
            @contactToMerge={{this.contactToMerge}}
            @changeset={{this.changeset}}
            @onCompleted={{this.handleMergingCompleted}}
            @onError={{this.handleMergingError}}
          />
        </div>
      {{else if (eq this.stage 'mergingError')}}
        <div class='p-3'>
          <GenericError
            @useLeftBorderCard={{true}}
          />
          <button
            {{on 'click' this.close}}
            class='btn btn-sm btn-outline-brand mt-3'
            type='button'
          >
            Close
          </button>
        </div>
      {{/if}}
      <ModalFooter class='d-flex'>
        <div>&nbsp;</div>
        {{#if (notEq this.stage 'merging')}}
          <div class='ml-auto'>
            <button
              {{on 'click' this.close}}
              class='btn btn-xs btn-link pr-0'
              type='button'
            >
              Cancel
            </button>
          </div>
        {{/if}}
      </ModalFooter>
    </div>
  </template>
}
